import { useState, useEffect } from 'react';
import Layout from './Layout';
import { useAuth } from '../hooks/useAuth';
import { createAuthenticatedAxios } from '../utils/auth';
import '../styles.css';
import ErrorBoundary from './ErrorBoundary';
import { API_URL } from '../config';

function QuestionOfTheDayMobile() {
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
  
  // Core state
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' }); // 'success', 'error', or null
  const [skipCount, setSkipCount] = useState({ skipsRemaining: 7, skipsUsed: 0 });
  const [isLoading, setIsLoading] = useState(true);

  // Load question and user data
  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    loadQuestion();
  }, [isAuthenticated, authLoading]);

  const loadQuestion = async () => {
    setIsLoading(true);
    setStatus({ type: null, message: '' });
    
    try {
      const axios = createAuthenticatedAxios(logout);
      
      // Load all data in parallel
      const [questionRes, skipRes] = await Promise.all([
        axios.get('/api/question/daily'),
        axios.get('/api/question/skip-count')
      ]);
      
      setQuestion(questionRes.data);
      setSkipCount(skipRes.data);
      
      console.log('Question loaded:', questionRes.data);
    } catch (err) {
      console.error('Failed to load question:', err);
      setStatus({ 
        type: 'error', 
        message: err.response?.data?.error || 'Failed to load question. Please refresh.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission (works with both click and touch)
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    
    console.log('Submit handler called');
    console.log('Answer:', answer);
    console.log('Question:', question);
    
    // Validation
    if (!answer.trim()) {
      setStatus({ type: 'error', message: 'Please enter an answer' });
      return;
    }
    
    if (!question || !question.id) {
      setStatus({ type: 'error', message: 'Question not loaded. Please refresh the page.' });
      return;
    }
    
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });
    
    try {
      console.log('Submitting answer...');
      const axios = createAuthenticatedAxios(logout);
      
      const response = await axios.post('/api/answer', {
        questionId: question.id,
        text: answer.trim()
      });
      
      console.log('Answer submitted successfully:', response.data);
      
      setStatus({ type: 'success', message: 'Answer saved!' });
      setAnswer('');
      
      // Redirect after short delay to show success message
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);
      
    } catch (err) {
      console.error('Submit error:', err);
      
      let errorMsg = 'Failed to save answer. ';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg += 'Request timed out. Please check your connection.';
      } else if (err.response?.status === 401) {
        errorMsg += 'Session expired. Please refresh and try again.';
      } else if (err.response?.status === 500) {
        errorMsg += 'Server error. Please try again.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg += err.message;
      }
      
      setStatus({ type: 'error', message: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle skip question
  const handleSkip = async (e) => {
    e.preventDefault();
    
    if (skipCount.skipsRemaining <= 0) {
      setStatus({ type: 'error', message: 'Daily skip limit reached (7 per day)' });
      return;
    }
    
    if (!question || !question.id) {
      setStatus({ type: 'error', message: 'Question not loaded' });
      return;
    }
    
    setIsSkipping(true);
    
    try {
      const axios = createAuthenticatedAxios(logout);
      
      await axios.post('/api/question/skip', { questionId: question.id });
      
      // Reload question
      await loadQuestion();
      setAnswer('');
      setStatus({ type: 'success', message: 'Question skipped!' });
      
      setTimeout(() => setStatus({ type: null, message: '' }), 2000);
    } catch (err) {
      if (err.response?.status === 429) {
        setStatus({ type: 'error', message: 'Daily skip limit reached' });
        setSkipCount({ skipsRemaining: 0, skipsUsed: err.response.data.skipsUsed });
      } else {
        setStatus({ type: 'error', message: err.response?.data?.error || 'Failed to skip question' });
      }
    } finally {
      setIsSkipping(false);
    }
  };

  // Clear status message after delay
  useEffect(() => {
    if (status.type === 'error') {
      const timer = setTimeout(() => {
        setStatus({ type: null, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  if (authLoading || isLoading) {
    return (
      <Layout>
        <div className="qotd-container container">
          <p className="loading">Loading question...</p>
        </div>
      </Layout>
    );
  }

  if (!question) {
    return (
      <Layout>
        <div className="qotd-container container">
          <h1>Question of the Day</h1>
          {status.type === 'error' && (
            <p className="error" style={{ marginBottom: '20px' }}>{status.message}</p>
          )}
          <button 
            onClick={loadQuestion}
            style={{
              padding: '12px 24px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <ErrorBoundary>
      <Layout>
        <div className="qotd-container container">
          <h1>Question of the Day</h1>
          
          {/* Status messages */}
          {status.type === 'success' && (
            <div style={{
              padding: '12px',
              backgroundColor: '#d4edda',
              color: '#155724',
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid #c3e6cb'
            }}>
              {status.message}
            </div>
          )}
          
          {status.type === 'error' && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '6px',
              marginBottom: '20px',
              border: '1px solid #f5c6cb'
            }}>
              {status.message}
            </div>
          )}
          
          {/* Skip count */}
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'center',
            fontSize: '14px',
            color: '#666'
          }}>
            Skips remaining: <strong>{skipCount.skipsRemaining}/7</strong>
          </div>
          
          {/* Question */}
          <p className="question-text" style={{ marginBottom: '20px' }}>
            {question.text}
          </p>
          
          {/* Form - using form element for better mobile support */}
          <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '100%' }}>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Type your answer here..."
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '15px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '16px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                resize: 'vertical',
                minHeight: '120px',
                maxWidth: '100%'
              }}
              disabled={isSubmitting}
            />
            
            {/* Buttons - using both onClick and onTouchStart for mobile */}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              flexDirection: 'column',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <button
                type="submit"
                disabled={isSubmitting || !answer.trim()}
                onTouchStart={(e) => {
                  // Ensure touch events work on mobile
                  if (!isSubmitting && answer.trim()) {
                    e.currentTarget.click();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: isSubmitting || !answer.trim() ? '#ccc' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '18px',
                  fontWeight: '600',
                  cursor: isSubmitting || !answer.trim() ? 'not-allowed' : 'pointer',
                  minHeight: '50px', // Large touch target for mobile
                  transition: 'background-color 0.2s',
                  WebkitTapHighlightColor: 'transparent', // Remove tap highlight on mobile
                  boxSizing: 'border-box'
                }}
              >
                {isSubmitting ? 'Saving...' : 'Submit Answer'}
              </button>
              
              <button
                type="button"
                onClick={handleSkip}
                onTouchStart={(e) => {
                  if (!isSkipping && skipCount.skipsRemaining > 0) {
                    e.currentTarget.click();
                  }
                }}
                disabled={isSkipping || skipCount.skipsRemaining <= 0}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: isSkipping || skipCount.skipsRemaining <= 0 ? '#ccc' : '#ff9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: isSkipping || skipCount.skipsRemaining <= 0 ? 'not-allowed' : 'pointer',
                  minHeight: '44px', // Large touch target
                  WebkitTapHighlightColor: 'transparent',
                  boxSizing: 'border-box'
                }}
              >
                {isSkipping ? 'Skipping...' : 'Skip Question'}
              </button>
            </div>
          </form>
        </div>
      </Layout>
    </ErrorBoundary>
  );
}

export default QuestionOfTheDayMobile;

